import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useForm, Controller } from 'react-hook-form';
import { Box, CircularProgress, Grid, TextField } from '@mui/material';
import { Lock } from '@mui/icons-material';

import SelectComponent from 'components/SelectComponent';
import CelMaskCustomComponent from 'components/MasksComponents/CelMaskCustomComponent';
import SuccessButtonComponent from 'components/Buttons/SuccessButtonComponent';
import { ConvertToSelectComponent } from 'components/SelectComponent/ConvertToSelectComponent';
import { sendEvent } from 'services/middleware/eventService';
import {
  isSalesExecutiveSession,
  getSalesExecutiveCode,
} from 'utils/auth/authHelper';
import { saveLead, getAgeRanges } from 'utils/lead';
import { saveState } from 'utils/localStorage';
import { gtmEventTrigger } from 'utils/gtagUtils';

import {
  emailableRuleChecker,
  generalNameProperties,
  generalPhoneProperties,
  generalRequiredWord,
} from 'utils/useForm/register';

SubscriptionForm.propTypes = {
  buttonId: PropTypes.string,
  buttonText: PropTypes.string,
  onSubmit: PropTypes.instanceOf(Function),
  onSuccess: PropTypes.instanceOf(Function),
  plan: PropTypes.string.isRequired,
};

SubscriptionForm.defaultProps = {
  buttonId: 'subButtonForm',
  buttonText: 'Solicitar orçamento',
  onSubmit: () => {},
  onSuccess: () => {},
};

export default function SubscriptionForm({
  buttonId,
  buttonText,
  onSubmit,
  onSuccess,
  plan,
}) {
  const { user } = useUser();
  const router = useRouter();
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
  } = useForm({
    mode: 'onBlur',
  });

  const [salesExecutiveCode, setSalesExecutiveCode] = useState(null);
  const cookies = {
    usertokenHubSpot: Cookies.get('hubspotutk'),
    pp: Cookies.get('pp') ? JSON.parse(Cookies.get('pp')) : {},
  };
  const [emailChecker, setEmailChecker] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    phone: '+55',
    age: 0,
  });

  useEffect(() => {
    if (user && isSalesExecutiveSession(user)) {
      setSalesExecutiveCode(getSalesExecutiveCode(user));
    }
  }, [user]);

  useEffect(() => {
    register('name', { name: 'referralCode' });

    setValue('referralCode', router?.query?.ivt);
  }, [register, router?.query?.ivt, setValue]);

  const formChange = (ref, value) => {
    setValue(ref, value);
    setForm({ ...form, [ref]: value });
  };

  const onHandleSubmit = (formData) => {
    setLoading(true);

    const student = {
      age: formData.age,
      channel: 'b2c_subscription',
      crmIntegrated: true,
      email: formData.email,
      firstname: formData.firstName,
      phone: formData.phone,
      plan,
    };

    const subscriptionLead = {
      acquireUrl: window.location.href,
      affiliateCode: cookies?.pp?.pcrid,
      affiliateId: router?.query?.affiliate,
      age: formData.age,
      campaign: router?.query?.utm_campaign,
      channel: 'b2c_subscription',
      content: router?.query?.utm_content,
      crmIntegrated: true,
      device: router?.query?.utm_device,
      email: formData.email,
      firstname: formData.firstName,
      gclid: router?.query?.gclid,
      medium: router?.query?.utm_medium,
      phone: formData.phone,
      plan,
      referralCode: formData.referralCode,
      saleExecutiveCode: parseInt(formData.salesExecutiveCode, 10),
      siteSourceName: router?.query?.site_source_name,
      source: router?.query?.utm_source,
      term: router?.query?.utm_term,
      trialCode: router?.query?.cp,
      usertokenHubSpot: cookies?.usertokenHubSpot,
    };

    saveLead(student, subscriptionLead).then((res) => {
      setLoading(false);

      if (res.data.validEmail) {
        onSuccess();
        saveState({ subscriptionLead });
        sendEvent('Lead');
        onSubmit();
        gtmEventTrigger('widget-subscription-lead-capture');
      } else {
        setError('email');
        setEmailChecker('E-mail inválido, tente novamente.');
      }
    });
  };

  return (
    <Box>
      <Box
        autoComplete="off"
        component="form"
        id="widget-subscription-lead-capture"
        onSubmit={handleSubmit(onHandleSubmit)}
      >
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={6}>
            <TextField
              aria-labelledby="name"
              error={!!errors?.firstName}
              fullWidth
              helperText={errors?.firstName?.message}
              id="name"
              inputProps={{ maxLength: 40 }}
              placeholder="Primeiro Nome"
              InputLabelProps={{
                shrink: true,
              }}
              {...register('firstName', {
                ...generalNameProperties,
                onChange: (e) => formChange('firstName', e.target.value),
              })}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <SelectComponent
              aria-labelledby="age"
              change={(value) => formChange('age', value)}
              disableMargin
              disableLeft
              error={!!errors?.age}
              helperText={errors?.age?.message}
              id="age"
              isDuplicateText={false}
              items={ConvertToSelectComponent(getAgeRanges(), 'value', 'text')}
              placeholder="Idade"
              value={form.age}
              styles={{ opacity: form.age === 0 && !errors?.age ? 0.5 : 1 }}
              inputRef={{
                ...register('age', {
                  required: 'Selecione sua idade',
                  min: {
                    value: 1,
                    message: 'Selecione sua idade',
                  },
                }),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: generalPhoneProperties.validate,
              }}
              render={({ field }) => (
                <CelMaskCustomComponent
                  {...field}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors?.phone}
                  fullWidth
                  helperText={errors?.phone?.message}
                  id="phone"
                  placeholder="Digite seu Celular"
                  onChange={(e) => formChange('phone', e)}
                  value={form.phone}
                  variant="outlined"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="email"
              aria-labelledby="email"
              error={!!errors?.email}
              helperText={errors?.email?.message || emailChecker}
              inputProps={{ maxLength: 100, type: 'email' }}
              placeholder="Digite seu email@aqui.com"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              {...register('email', {
                ...generalRequiredWord('Email'),
                validate: (value) => emailableRuleChecker(value),
                onChange: (e) =>
                  formChange('email', e.target.value.toLowerCase()),
              })}
            />
          </Grid>

          {salesExecutiveCode && (
            <input
              {...register('salesExecutiveCode')}
              type="hidden"
              value={salesExecutiveCode}
            />
          )}

          <Grid item xs={12}>
            <Box mt={1} position="relative">
              <SuccessButtonComponent
                disabled={loading}
                fullWidth
                id={buttonId}
                size="large"
                startIcon={<Lock />}
                type="submit"
                variant="contained"
              >
                {buttonText}
              </SuccessButtonComponent>

              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    left: '50%',
                    marginLeft: '-12px',
                    marginTop: '-12px',
                    position: 'absolute',
                    top: '50%',
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
